import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingBag, MapPin, ArrowLeft } from 'lucide-react';

const cityInfo = {
  nicosia: { name: 'Nicosia', nameGr: 'Λευκωσία' },
  larnaca: { name: 'Larnaca', nameGr: 'Λάρνακα' },
  limassol: { name: 'Limassol', nameGr: 'Λεμεσός' },
  paphos: { name: 'Paphos', nameGr: 'Πάφος' },
  ammochostos: { name: 'Ammochostos', nameGr: 'Αμμόχωστος' },
};

export default function CityOptions() {
  const { city } = useParams();
  const cityData = cityInfo[city];

  useEffect(() => {
    document.title = `${cityData?.name || 'City'} - Salt & Stone`;
  }, [cityData]);

  if (!cityData) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-[#F2F0E9] text-2xl mb-4">City not found</h1>
          <Link to="/find" className="text-[#D4AF37] hover:underline">
            Back to regions
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="city-options-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="lg:hidden absolute left-1/2 -translate-x-1/2 top-0 -translate-y-[40%] h-[95vh] w-[90vw]">
            <img
              src="https://customer-assets.emergentagent.com/job_60caa5be-1d50-4b72-801b-3659f641dade/artifacts/9uzc6zo8_IMG_7292.jpg"
              alt="Salt & Stone Gin Bottle"
              className="h-full w-full object-contain opacity-40"
            />
            <div className="absolute inset-0 pointer-events-none" style={{
              background: 'radial-gradient(ellipse 80% 70% at center 40%, transparent 45%, #000000 85%)'
            }}></div>
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-transparent to-black"></div>
          </div>
          <img
            src="https://customer-assets.emergentagent.com/job_60caa5be-1d50-4b72-801b-3659f641dade/artifacts/9uzc6zo8_IMG_7292.jpg"
            alt="Salt & Stone Gin Bottle"
            className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 h-full w-auto max-w-none object-contain opacity-80 translate-x-[10%]"
          />
          <div className="hidden lg:block absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/60 to-transparent"></div>
          <div className="hidden lg:block absolute inset-0 bg-gradient-to-b from-[#050505]/30 via-transparent to-[#050505]/80"></div>
          <div className="lg:hidden absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto pt-24">
          {/* Back Button */}
          <Link 
            to="/find" 
            className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#F2F0E9] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Regions
          </Link>

          <div className="flex items-center justify-center gap-3 mb-4">
            <MapPin className="w-6 h-6 text-[#D4AF37]" />
            <span className="caption">{cityData.nameGr}</span>
          </div>
          
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl text-[#F2F0E9] mb-6 animate-fade-in opacity-0" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
            {cityData.name}
          </h1>
          <p className="text-[#F2F0E9]/70 text-lg md:text-xl mb-12 max-w-2xl mx-auto animate-fade-in opacity-0" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
            How would you like to get Salt & Stone?
          </p>
          
          {/* Two Options */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center animate-fade-in opacity-0" style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}>
            <Link 
              to={`/find/${city}/online`}
              className="btn-primary inline-flex items-center justify-center gap-3 px-10 py-4"
              data-testid="shop-online-btn"
            >
              <ShoppingBag className="w-5 h-5" />
              Shop Online
            </Link>
            <Link 
              to={`/find/${city}/stores`}
              className="btn-secondary inline-flex items-center justify-center gap-3 px-10 py-4"
              data-testid="find-stores-btn"
            >
              <MapPin className="w-5 h-5" />
              Find in Store
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
